import React, { useEffect, useRef, useState } from "react";
import { FaAngleDown, FaSortUp, FaSortDown } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { TbEye, TbEyeClosed, TbSearch, TbEyeEdit } from "react-icons/tb";
import "./Table.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

const Table = ({
  data,
  columns,
  theme,
  onRowClick,
  onImport = null,
  onExport = null,
  viewImport = true,
  viewExport = true,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [filters, setFilters] = useState({});
  // Clave de localStorage para la persistencia de columnas
  const localStorageKey = "tableVisibleColumns_" + columns.map(c => c.key).join("_");

  const handleRowClick = typeof onRowClick === "function" ? onRowClick : null;

  // Estado para las columnas visibles, inicializado a partir de localStorage si existe
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem(localStorageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // solo lo usamos si es un array con al menos un elemento
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      } catch {
        // si JSON falla, lo ignoramos
      }
    }
    // en cualquier otro caso mostramos todas las columnas
    return columns.map((col) => col.key);
  });

  // Cada vez que visibleColumns cambia, se guarda en localStorage
  useEffect(() => {
    localStorage.setItem(localStorageKey, JSON.stringify(visibleColumns));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumns]);

  // Estado para el reordenamiento de columnas (drag and drop)
  const [columnOrder, setColumnOrder] = useState(() =>
    Array.isArray(columns) ? columns.map((col) => col.key) : []
  );

  // Sincronizar columnOrder y visibleColumns cuando cambian las columnas (props)
  // Esto soluciona problemas cuando se agregan columnas dinámicamente o por código
  useEffect(() => {
    const propKeys = columns.map(c => c.key);

    // Detectar si hay cambios estructurales (nuevas columnas o columnas eliminadas)
    // Compara si el conjunto de claves en el estado coincide con las props
    const hasStructureChanged =
      propKeys.length !== columnOrder.length ||
      !propKeys.every(key => columnOrder.includes(key));

    if (hasStructureChanged) {
      console.log("Table structure changed, syncing state...", propKeys);
      // Si cambia la estructura, reseteamos el orden al orden de las props
      setColumnOrder(propKeys);

      // Y actualizamos las columnas visibles para incluir las nuevas por defecto
      setVisibleColumns(prevVisible => {
        // Mantenemos las que ya eran visibles Y siguen existiendo en las nuevas props
        const kept = prevVisible.filter(key => propKeys.includes(key));
        // Encontramos las nuevas (que están en props pero no estaban en el estado de orden anterior)
        const newCols = propKeys.filter(key => !columnOrder.includes(key));

        const combined = [...kept, ...newCols];
        return combined.length > 0 ? combined : propKeys;
      });
    }
  }, [columns, columnOrder]);

  const [openVisualizador, setOpenVisualizador] = useState(false);

  const theadRef = useRef(null);
  const [theadPosition, setTheadPosition] = useState({ top: 0, left: 0 });
  useEffect(() => {
    const updateTheadPosition = () => {
      if (theadRef.current) {
        const rect = theadRef.current.getBoundingClientRect();
        setTheadPosition({ top: rect.bottom, left: rect.left });
      }
    };

    if (openVisualizador) {
      updateTheadPosition(); // calcular al abrir
      window.addEventListener("scroll", updateTheadPosition, true); // true para capturar scroll en elementos con overflow
      window.addEventListener("resize", updateTheadPosition);
    }

    return () => {
      window.removeEventListener("scroll", updateTheadPosition, true);
      window.removeEventListener("resize", updateTheadPosition);
    };
  }, [openVisualizador]);


  // Función para manejar la visibilidad de las columnas
  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns((prevVisibleColumns) =>
      prevVisibleColumns.includes(columnKey)
        ? prevVisibleColumns.filter((key) => key !== columnKey)
        : [...prevVisibleColumns, columnKey]
    );
  };

  // Drag and drop: al terminar el arrastre, actualizamos el orden
  const onDragEnd = (result) => {
    if (!result.destination) return;

    // Obtenemos el orden de las columnas visibles
    const visibleOrder = columnOrder.filter((key) =>
      visibleColumns.includes(key)
    );

    // Creamos una copia del orden visible y reordenamos según el resultado
    const newVisibleOrder = Array.from(visibleOrder);
    const [removed] = newVisibleOrder.splice(result.source.index, 1);
    newVisibleOrder.splice(result.destination.index, 0, removed);

    // Reconstruimos columnOrder: para cada key en columnOrder que sea visible,
    // reemplazamos su posición con los elementos de newVisibleOrder en orden.
    let newColumnOrder = columnOrder.slice();
    let j = 0;
    for (let i = 0; i < newColumnOrder.length; i++) {
      if (visibleColumns.includes(newColumnOrder[i])) {
        newColumnOrder[i] = newVisibleOrder[j];
        j++;
      }
    }
    setColumnOrder(newColumnOrder);
  };

  // Función para manejar el orden
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Función para obtener el icono de ordenación
  const getSortIcon = (columnName) => {
    if (sortConfig.key === columnName) {
      return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
    }
    return null;
  };

  // Función para manejar los filtros
  const handleFilterChange = (e, columnKey) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [columnKey]: e.target.value,
    }));
  };

  // Ordenar los datos según la configuración de orden
  const sortedData = Array.isArray(data) ? [...data].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    if (aValue < bValue) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  }) : [];

  const [globalSearch, setGlobalSearch] = useState("");

  // Filtrar los datos por los filtros establecidos
  const filteredData = sortedData.filter((item) => {
    // Filtros de columna
    const passesColumnFilters = Object.keys(filters).every((key) => {
      if (!filters[key]) return true;
      return item[key]
        ?.toString()
        .toLowerCase()
        .includes(filters[key].toLowerCase());
    });

    // Buscador global: revisa si alguna propiedad del item (convertida a string)
    // contiene el texto global (solo en minúsculas)
    const passesGlobalSearch =
      globalSearch.trim() === "" ||
      Object.keys(item).some((key) => {
        return item[key]
          ?.toString()
          .toLowerCase()
          .includes(globalSearch.toLowerCase());
      });

    return passesColumnFilters && passesGlobalSearch;
  });

  // Función para formatear las fechas
  const getFormattedDateCellContent = (value) => {
    const dateValue = value ? value.split(" ")[0] : "En proceso";

    return (
      <td
        style={{
          backgroundColor: theme === "dark" ? "#242424" : "#ffffffff",
          color: theme === "dark" ? "#F1F8E9" : "#000",
          height: "40px",
        }}
      >
        {dateValue}
      </td>
    );
  };

  // Función para determinar si hay un valor o no
  const getCellContent = (value) => {
    if (value === "N/A" || value == null || value === "") {
      return (
        <td
          style={{
            backgroundColor: theme === "dark" ? "#242424" : "#ffffffff",
            color: theme === "dark" ? "#c3cbdc66" : "#00000066",
            height: "40px",
            padding: "0.4rem 0.5rem",
            whiteSpace: "normal",
            wordBreak: "break-word",
            fontSize: "0.85rem",
          }}
        >
          <span style={{ color: "gray" }}>N/A</span>
        </td>
      );
    }
    return (
      <td
        style={{
          backgroundColor: theme === "dark" ? "#242424" : "#ffffffff",
          height: "40px",
          padding: "0.4rem 0.5rem",
          whiteSpace: "normal",
          wordBreak: "break-word",
          fontSize: "0.85rem",
        }}
      >
        {value}
      </td>
    );
  };

  // Función para aplicar estilo especial a la columna de "estado" si es ListaCasos
  const statusColors = {
    FINALIZADO: { text: "#09cc09", background: "#09ce0933" },
    EN_PROCESO: { text: "#915eef", background: "#915eef33" },
    ASIGNADO: { text: "#49b0ea", background: "#49b0ea4d" },
    PENDIENTE: { text: "#bf1b21", background: "#bf1b2133" },
    SIN_ASIGNAR: { text: "#e16b20", background: "#e16b2033" },
    EN_PAUSA: { text: "#3272b1", background: "#3272b133" },
    ACTIVO: { text: "#5c6bc0", background: "#5c6bc01a" },
    VENCIDO: { text: "#bf1b21", background: "#bf1b2133" },
    EN_GESTION: { text: "#915eef", background: "#915eef33" },
  };

  const getEstadoCellContent = (value) => {
    // Si value es falsy (null, undefined o cadena vacía), se asigna "ACTIVO"
    const finalValue = value ? value : "ACTIVO";
    const color = statusColors[finalValue] || { text: "", background: "" };
    return (
      <td
        style={{
          backgroundColor: theme === "dark" ? "#242424" : "#ffffffff",
          color: color.text,
          height: "40px",
          padding: "0.4rem 0.5rem",
        }}
      >
        <span
          style={{
            borderRadius: "4px",
            padding: "2px 8px",
            fontSize: "0.7rem",
            fontWeight: "600",
            backgroundColor: color.background,
            textTransform: "uppercase",
            letterSpacing: "0.3px",
          }}
        >
          {finalValue}
        </span>
      </td>
    );
  };

  return (
    <div
      style={{
        padding: "0rem 1rem",
        width: "100%",
        maxWidth: "100vw",
        overflow: "hidden",
      }}
    >
      <article
        className={`contain_actions_table ${theme === "dark"
          ? "contain_actions_table_dark"
          : "contain_actions_table_light"
          }`}
        style={{
          width: "100%",
          maxWidth: "100%",
          marginBottom: "1rem",
        }}
      >
        <div className="content_actions_table">
          <div>
            <div style={{ position: "relative" }} className="">
              <input
                type="text"
                className={`${theme === "dark"
                  ? "input_search_table_dark"
                  : "input_search_table"
                  }`}
                name="search"
                placeholder="Buscar..."
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                autoComplete="off"
              />
              {!globalSearch && (
                <div className="button_search_table">
                  <TbSearch size={16} strokeWidth={2.8} />
                </div>
              )}
              {globalSearch && (
                <div
                  className="button_x_table_"
                  onClick={() => setGlobalSearch("")}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              )}
            </div>
          </div>

          <div className="buttons_actions_table">
            <div className="content_button_import_">
              {viewImport && (
                <button
                  className={`button_importexport_table ${theme === "dark"
                    ? "button_import_export_dark"
                    : "button_import_export_light"
                    }`}
                  onClick={() => onImport && onImport()}
                >
                  Importar
                </button>
              )}
              {viewExport && (
                <button
                  className={`button_importexport_table ${theme === "dark"
                    ? "button_import_export_dark"
                    : "button_import_export_light"
                    }`}
                  onClick={() => onExport && onExport()}
                >
                  Exportar
                </button>
              )}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <button
                className={`button_content_view_columns ${theme === "dark"
                  ? "button_content_view_columns_dark"
                  : "button_content_view_columns_light"
                  }`}
                onClick={() => setOpenVisualizador(!openVisualizador)}
              >
                {/* <TbDotsVertical size={19} />{" "} */}
                <TbEyeEdit size={20} strokeWidth={1.5} />
                Visualizar
              </button>
            </div>
          </div>
        </div>
      </article>
      {/* Contenedor con scroll horizontal para responsividad */}
      <div
        style={{
          width: "100%",
          overflowX: "auto",
          overflowY: "auto",
          maxHeight: "100vh",
          borderRadius: "10px",
          border: theme === "dark" ? "1px solid #3b3b3b" : "1px solid #F1F1F4",
          boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, .05)",
          // Estilos para el scrollbar
          scrollbarWidth: "thin",
          scrollbarColor: theme === "dark" ? "#555 #333" : "#ccc #f1f1f1",
        }}
        className="table-responsive-container"
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <table
            className={`font-size-13 text-center ${theme === "dark" ? "table-dark" : "table-light"
              }`}
            responsive
            hover
            style={{
              position: "relative",
              borderRadius: "0",
              border: "none",
              minWidth: "800px", // Ancho mínimo para mantener legibilidad
              width: "100%",
              margin: 0,
            }}
          >
            <Droppable droppableId="columns" direction="horizontal">
              {(provided) => (
                <thead
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    height: "36px",
                    backgroundColor: theme === "dark" ? "#3b3b3b" : "#C8E6C9",
                    borderBottom:
                      theme === "dark"
                        ? "1px solid #3b3b3b"
                        : "1px solid #F1F1F4",
                    position: "sticky",
                    top: 0,
                    zIndex: 30,
                  }}
                >
                  <tr ref={theadRef}>
                    {columnOrder
                      .filter((key) => visibleColumns.includes(key))
                      .map((columnKey, index) => {
                        const column = columns.find(
                          (col) => col.key === columnKey
                        );
                        if (!column) return null;
                        return (
                          <Draggable
                            key={column.key}
                            draggableId={column.key}
                            index={index}
                          >
                            {(provided) => (
                              <th
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  padding: "0.2rem 0.5rem",
                                  fontSize: "0.85rem",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: ".5rem",
                                  }}
                                >
                                  {getSortIcon(column.key)}
                                  <span
                                    style={{
                                      cursor: "pointer",
                                      fontWeight: "500",
                                    }}
                                    onClick={() => requestSort(column.key)}
                                  >
                                    {column.label}
                                  </span>
                                  {column.filterable && (
                                    <FaAngleDown
                                      style={{ cursor: "pointer" }}
                                      onClick={() =>
                                        setFilters((prev) => ({
                                          ...prev,
                                          [column.key]: "",
                                        }))
                                      }
                                    />
                                  )}
                                </div>
                                {filters[column.key] && (
                                  <div>
                                    <input
                                      type="text"
                                      value={filters[column.key] || ""}
                                      onChange={(e) =>
                                        handleFilterChange(e, column.key)
                                      }
                                      placeholder={`Filtrar por ${column.label}`}
                                    />
                                  </div>
                                )}
                              </th>
                            )}
                          </Draggable>
                        );
                      })}
                    {provided.placeholder}
                  </tr>
                </thead>
              )}
            </Droppable>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((row, index) => (
                  <tr
                    key={index}
                    style={{
                      cursor: "pointer",
                      borderBottom:
                        theme === "dark"
                          ? "1px solid #3b3b3b"
                          : "1px solid #eff2f7",
                    }}
                    onMouseEnter={(e) => {
                      if (theme !== 'dark') e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      if (theme !== 'dark') e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    onClick={
                      handleRowClick ? () => handleRowClick(row) : undefined
                    }
                  >
                    {columnOrder
                      .filter((key) => visibleColumns.includes(key))
                      .map((columnKey) => {
                        const column = columns.find(
                          (col) => col.key === columnKey
                        );
                        if (!column) return null;
                        if (column.key === "estado" || column.key === "status") {
                          return getEstadoCellContent(row[column.key]);
                        } else if (
                          column.key === "created_date" ||
                          column.key === "fecha_finalizacion"
                        ) {
                          return getFormattedDateCellContent(row[column.key]);
                        } else {
                          return getCellContent(row[column.key]);
                        }
                      })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={visibleColumns.length}
                    style={{
                      textAlign: "center",
                      padding: "1rem",
                      backgroundColor: theme === "dark" ? "#242424" : "#ffffffff",
                      color: theme === "dark" ? "#F1F8E9" : "#000",
                    }}
                  >
                    No se encontraron datos
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </DragDropContext>
      </div>

      {/* Estilos dinámicos para el scrollbar según el tema */}
      <style jsx>{`
         .table-responsive-container::-webkit-scrollbar-track {
           background: ${theme === "dark" ? "#333" : "#f1f1f1"};
         }
         .table-responsive-container::-webkit-scrollbar-thumb {
           background: ${theme === "dark" ? "#555" : "#ccc"};
         }
         .table-responsive-container::-webkit-scrollbar-thumb:hover {
           background: ${theme === "dark" ? "#777" : "#999"};
         }
       `}</style>
      {openVisualizador && (
        <div className="overlay_visualizador">
          <div
            style={{ position: "absolute", top: theadPosition.top + 10 }}
            className={`${openVisualizador
              ? "content_view_columns_open"
              : "content_view_columns"
              } card_background `}
          >
            <div className="content_opcions_view_columns">
              {columns.map((column) => (
                <div
                  key={column.key}
                  className={`opcions_view_columns ${theme === "dark"
                    ? "opcions_view_columns_dark"
                    : "opcions_view_columns_light"
                    }`}
                  style={{
                    color: visibleColumns.includes(column.key) ? "#2fa4e7" : "",
                  }}
                  onClick={() => toggleColumnVisibility(column.key)}
                >
                  <label className="label_view_column">{column.label}</label>
                  <div className="icon_view_column">
                    {visibleColumns.includes(column.key) ? (
                      <TbEye size={20} strokeWidth={1.5} />
                    ) : (
                      <TbEyeClosed size={20} strokeWidth={1.5} />
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="content_button_ok_visua">
              <button
                onClick={() => setOpenVisualizador(!openVisualizador)}
                className="button_ok_visua"
              >
                Hecho
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
